import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '@treams/treams-icons';
import {
	Avatar,
	Button,
	Empty,
	Heading,
	Input,
	Label,
	Paragraph,
	Skeleton,
	Subheading,
	Toolbar,
} from '@treams/treams-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import * as _ from 'lodash';
import { rgba } from 'polished';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import useSearch from 'packages/kakapo-ui-application/hooks/useSearch';
import { Link } from '@treams/treams-routes';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';

const Result = styled(Link)`
	display: block;
	cursor: pointer;
	padding: 5px;
	margin: -5px;
	border-radius: 5px;

	&:hover {
		background: ${(props) => rgba(props.theme.colors.lightBlueLight, 0.5)};

		&:active {
			background: ${(props) => props.theme.colors.lightBlueLight};
		}
	}

	svg {
		display: block;
		width: 24px;
		height: 24px;

		path {
			fill: ${(props) => props.theme.colors.lightBlueDark};
		}
	}
`;

function ComplimentRecipient(props: { user: graphql.User }) {
	const { localize } = useLocalize();

	const jobTitle = props.user.jobPositions?.[0]?.jobTitle.title;

	return (
		<Box>
			<Result to={`/users/${props.user.id}/feedback/compliments/new`}>
				<Flex alignItems="center">
					<Avatar user={props.user} size={48} mr={2} flexGrow={0} flexShrink={0} />
					<Box flexGrow={1} flexShrink={1}>
						<Paragraph mb={1}>
							{props.user.firstName} {props.user.lastName}
						</Paragraph>
						{jobTitle && <Label>{localize(jobTitle).text}</Label>}
					</Box>
					<Box flexGrow={0} flexShrink={0}>
						<ChevronRightIcon />
					</Box>
				</Flex>
			</Result>
		</Box>
	);
}

function ComplimentRecipientSkeleton() {
	return (
		<Flex>
			<Avatar.Skeleton size={48} mr={2} />
			<Box>
				<Paragraph.Skeleton lines={1} mb={1} />
				<Label.Skeleton />
			</Box>
		</Flex>
	);
}

const Grid = styled(Box)`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px 20px;
`;

export default function ComplimentRecipientSelector(props: { onDismiss: () => void }) {
	const { t } = useTranslation();

	const viewer = useAuthenticatedUser();

	const [query, setQuery] = useState('');

	const targets = graphql.useComplimentTargetsQuery();

	const users = _.sortBy(
		_.uniqBy(
			targets.data?.viewer.roles.flatMap((role) => {
				if (role.targetV2?.__typename === 'Group') {
					return role.targetV2.members.edges.map(({ node }) => node);
				} else {
					return [];
				}
			}),
			(user) => user.id,
		)
			.filter(({ isArchived }) => !isArchived)
			.filter(({ id }) => id !== viewer.id),
		(user) => `${user.firstName} ${user.lastName}`,
	);

	const { rawLoading, results } = useSearch(query, {
		skip: !query,
		filters: 'isArchived:false',
	});

	return (
		<Box p={3} width={600} maxHeight={600} overflowY="scroll">
			<Toolbar>
				<Button
					title={t('compliments.create.backButton')}
					type="secondary"
					icon={<ChevronLeftIcon />}
					onClick={props.onDismiss}
				/>
			</Toolbar>
			<Heading mt={4} mb={3}>
				{t('compliments.create.recipients.search.title')}
			</Heading>
			<Input
				search
				onChange={setQuery}
				value={query}
				autoFocus
				icon={<SearchIcon />}
				placeholder={t('compliments.create.recipients.search.placeholder')}
				mb={3}
			/>

			{rawLoading ? (
				<Grid>
					<Skeleton.Repeat count={2}>
						<ComplimentRecipientSkeleton />
					</Skeleton.Repeat>
				</Grid>
			) : results.length === 0 || !query ? (
				<Skeleton.Empty
					contents={
						<Empty
							small
							icon={<SearchIcon />}
							title={
								query
									? t('compliments.create.recipients.search.empty')
									: t('compliments.create.recipients.search.instruction')
							}
						/>
					}
				>
					<Grid>
						<Skeleton.Repeat count={2}>
							<ComplimentRecipientSkeleton />
						</Skeleton.Repeat>
					</Grid>
				</Skeleton.Empty>
			) : (
				<Grid>
					{results.slice(0, 10).map((user) => (
						<ComplimentRecipient key={user.id} user={user} />
					))}
				</Grid>
			)}

			<Subheading mt={5} mb={2}>
				{t('compliments.create.recipients.search.department')}
			</Subheading>
			<Grid>
				{targets.data ? (
					users.map((user) => <ComplimentRecipient key={user.id} user={user as graphql.User} />)
				) : (
					<Skeleton.Repeat count={5}>
						<ComplimentRecipientSkeleton />
					</Skeleton.Repeat>
				)}
			</Grid>
		</Box>
	);
}
